import { Injectable, inject, signal, computed } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export type Lang = 'en' | 'es';

export const AVAILABLE_LANGS: Lang[] = ['en', 'es'];

const STORAGE_KEY = 'dhats-lang';

/**
 * The interface language, kept the same way the theme is: a signal, a
 * localStorage key and a toggle in the header.
 *
 * Not to be confused with a project's own `language` field, which decides
 * which finding templates are offered and what language the PDF report comes
 * out in. That one belongs to the project and is nobody's personal preference.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly _lang = signal<Lang>(this.loadLang());

  readonly lang = this._lang.asReadonly();
  readonly isSpanish = computed(() => this._lang() === 'es');

  constructor() {
    this.transloco.setActiveLang(this._lang());
  }

  toggle(): void {
    this.set(this._lang() === 'en' ? 'es' : 'en');
  }

  set(lang: Lang): void {
    if (!AVAILABLE_LANGS.includes(lang)) return;
    this._lang.set(lang);
    this.transloco.setActiveLang(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // A browser with storage blocked still gets a working toggle for the
      // session; it just will not be remembered.
    }
  }

  private loadLang(): Lang {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (stored && AVAILABLE_LANGS.includes(stored)) return stored;
    } catch {
      // Ignore and fall through to the default.
    }
    return 'en';
  }
}
